import { cx } from '@linaria/core';
import { useCallback, useState } from 'react';

import { Captions } from '~components/Typography';
import type { PaymentType } from '~context/paywall/types';
import type {
	PaymentReason,
	PaymentReasonType,
} from '~features/annals/enums/payment';
import annalsCheckout from '~features/annals/hooks/checkout';
import { useTranslations } from '~i18n/hooks';
import CancelIcon from '~public/assets/icons/cancel.svg';
import { useApplyPromoCodeMutation } from '~services/api/credits';
import type {
	TShopItem,
	TShopPromoCodeResponse,
} from '~services/api/credits/types';
import { ButtonPrimary } from '~shared/ui/atoms/Buttons/ButtonPrimary';
import { PortableText } from '~shared/ui/integrations/Sanity';
import { InputField } from '~shared/ui/organisms/InputField';
import { isErrorWithMessageCap } from '~shared/utils/errors';

import { usePromocodeErrorConfig } from '../../hooks/usePromocodeErrorConfig';
import {
	deleteDefaultPromocode,
	getDefaultPromocode,
} from '../../promocode-storage';
import { checkIfApplicable } from '../../utils/checkIfApplicable';
import {
	input,
	OpenedContent,
	PromocodeButton,
	PromocodeWrapper,
} from './styles';

type TPromoCodeInputProps = {
	selectedItem: TShopItem;
	paymentType: PaymentType;
	reason?: PaymentReason;
	reasonType?: PaymentReasonType;
	defaultPromocode?: string;
	promoCodeInfo?: TShopPromoCodeResponse;
	onApplied: (data: TShopPromoCodeResponse | undefined) => void;
};

export const Promocode = ({
	selectedItem,
	reason,
	reasonType,
	paymentType,
	promoCodeInfo,
	defaultPromocode,
	onApplied,
}: TPromoCodeInputProps) => {
	const [initialPromocode] = useState(
		() => defaultPromocode ?? getDefaultPromocode()
	);
	const [promocode, setPromocode] = useState(initialPromocode || '');
	const [error, setError] = useState<string | undefined>(undefined);
	const [opened, setOpened] = useState(!!initialPromocode);
	const [send, { isLoading }] = useApplyPromoCodeMutation();
	const t = useTranslations('Promocode');

	const customErrorConfig = usePromocodeErrorConfig();

	const promoCodeApplied = annalsCheckout.usePromocodeApplied({
		reason,
		reasonType,
		type: paymentType,
	});

	const logData = useCallback(
		(data: TShopPromoCodeResponse | undefined) => {
			const extra = { promocodeId: data?.Id, Prices: data?.Prices };
			void promoCodeApplied(extra);
		},
		[promoCodeApplied]
	);

	const onPromoCodeApplied = async () => {
		setError(undefined);
		try {
			const data = await send({ PromoCode: promocode }).unwrap();
			deleteDefaultPromocode();
			if (!checkIfApplicable(selectedItem.sku, data.Prices)) {
				setError(customErrorConfig.NotApplicable);
				return;
			}
			logData(data);
			onApplied(data);
		} catch (err) {
			onApplied(undefined);
			if (isErrorWithMessageCap(err)) {
				const customError = customErrorConfig[err.data.Error];
				if (customError) {
					setError(customError);
					return;
				}
			}
			setError(customErrorConfig.NotFound);
		}
	};

	return (
		<PromocodeWrapper className={cx(opened && 'opened')}>
			<PromocodeButton
				as="span"
				onClick={() => setOpened((prev) => !prev)}
				color="grey-52-solid"
				size="XS"
			>
				<PortableText value={t.raw('promocodeButton')} />
			</PromocodeButton>
			{opened && (
				<>
					<OpenedContent>
						<InputField
							error={error}
							placeholder={t('promocodePlaceholder')}
							className={input}
							maxLength={16}
							value={promocode}
							onChange={(e) => setPromocode(e.target.value)}
							rightIcon={promocode ? <CancelIcon /> : null}
							onRightIconClick={() => setPromocode('')}
						/>
						<ButtonPrimary
							onClick={onPromoCodeApplied}
							disabled={!promocode || promocode === promoCodeInfo?.PromoCode}
							size="M"
							loading={isLoading}
						>
							{t('applyPromocodeButtonTitleV2')}
						</ButtonPrimary>
					</OpenedContent>
					{promoCodeInfo && (
						<Captions align="left" size="L" color="grey-68-solid">
							{t('promocodeAppliedTextV2', {
								discount: promoCodeInfo.DiscountPercent,
							})}
						</Captions>
					)}
				</>
			)}
		</PromocodeWrapper>
	);
};
